import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LockOutlined from "../../components/Atomos/Icons/LockOutlined";
import PencilEdit from "../../components/Atomos/Icons/PencilEdit";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import type { IHeader } from "../../components/Organismos/Table/FilteredTable.types";
import InputText from "../../components/staff/InputText";
import { getEventById } from "../../Services/Event";
import { getInvoiceHistory, listInvoiceFiles } from "../../Services/Invoice";
import {
  getRegistrationsByEvent,
  getRegistrationsByPerson,
} from "../../Services/Registration";
import type { IPagination2, ISorting } from "../../interfaces/Components/ITable";
import type { IEvent } from "../../interfaces/models/IEvent";
import type { IRegistration } from "../../interfaces/models/IRegistration";
import AuthContext from "../../store/AuthContext";
import { getDayKeyInTimezone } from "../../utils/dates";
import { isCurrentEvent } from "../../utils/eventTiming";
import { exportStyledExcel } from "../../utils/excel/exportStyledExcel";
import { formatEventLocation } from "../../utils/eventLocationFormatter";
import { jurisdictionValueToLabel } from "../../utils/jurisdiction/structuredJurisdiction";
import { formatEventDate } from "../../utils/eventDateFormatter";
import { downloadInvoicePDF } from "../../utils/generateInvoicePDF";
import { toastError, toastWarn } from "../../utils/toast";

type MyEventsTab = "current" | "past";
type UiPaymentStatus = "Paid" | "Unpaid" | "Partial";

interface BlobInvoiceFile {
  name: string;
  url: string;
  dateCreated?: string;
}

type InvoiceCandidate = {
  meetingId: string;
  contactId: string;
  type: "Paid" | "Due";
  amount: number;
  currency: string;
  invoice?: string;
  invoiceUrl?: string;
  dateSent?: string;
};

interface MyEventRow {
  id: string;
  rowNumber?: number;
  event: IEvent;
  registration: IRegistration;
  eventName: string;
  eventDate: string;
  eventDateFilename: string;
  location: string;
  uiStatus: UiPaymentStatus;
  sortDate: number | null;
  currentSearchText: string;
  pastSearchText: string;
}

const DEFAULT_SORTING_BY_TAB: Record<MyEventsTab, ISorting> = {
  current: { field: "eventDate", order: "asc" },
  past: { field: "eventDate", order: "desc" },
};

const DEFAULT_PAGINATION: IPagination2 = {
  pageIndex: 0,
  pageSize: 50,
};

const PAID_STATUSES = new Set(["completed", "paidspk", "paidspr"]);
const PARTIAL_STATUSES = new Set(["partial"]);
const PAYMENT_DUE_STATUSES = new Set([
  "pending",
  "partial",
  "unpaidspk",
  "unpaidspr",
]);
const EXCLUDED_ATTENDEE_FIRM_IDS = new Set([
  "ee153da4-bbf0-4c29-80cb-29a05905ae2c",
  "430be9f8-c9be-4f62-aec1-91e0596799b8",
]);

const normalizeStatus = (value?: string): string =>
  (value || "").trim().toLowerCase();

const isActiveRegistration = (registration: IRegistration): boolean =>
  registration.isDeleted !== true &&
  normalizeStatus(registration.status) !== "cancelled";

const mergeRegistrationsByEvent = (
  registrations: IRegistration[],
): Map<string, IRegistration> => {
  const registrationsByEvent = new Map<string, IRegistration>();

  registrations.filter(isActiveRegistration).forEach((registration) => {
    if (!registration.eventId) {
      return;
    }

    const current = registrationsByEvent.get(registration.eventId);
    if (
      !current ||
      (registration.registrationDate || 0) > (current.registrationDate || 0)
    ) {
      registrationsByEvent.set(registration.eventId, registration);
    }
  });

  return registrationsByEvent;
};

const resolveUiPaymentStatus = (paymentStatus?: string): UiPaymentStatus => {
  const normalized = normalizeStatus(paymentStatus);
  if (PARTIAL_STATUSES.has(normalized)) {
    return "Partial";
  }
  if (PAID_STATUSES.has(normalized)) {
    return "Paid";
  }
  return "Unpaid";
};

const isPaymentDue = (paymentStatus?: string): boolean =>
  PAYMENT_DUE_STATUSES.has(normalizeStatus(paymentStatus));

const getInvoiceTypeForStatus = (paymentStatus?: string): "Paid" | "Due" =>
  PAID_STATUSES.has(normalizeStatus(paymentStatus)) ? "Paid" : "Due";

const inferInvoiceTypeFromFilename = (filename?: string): "Paid" | "Due" => {
  const normalized = (filename || "").toLowerCase();
  return normalized.startsWith("paid_invoice") ? "Paid" : "Due";
};

const mergeInvoiceEntries = (
  history: InvoiceCandidate[],
  blobFiles: BlobInvoiceFile[],
): InvoiceCandidate[] => {
  const combined = history.map((entry) => ({
    ...entry,
    invoice: entry.invoice,
    invoiceUrl: entry.invoiceUrl,
  }));

  blobFiles.forEach((file) => {
    const existingEntry = combined.find(
      (entry) => entry.invoice === file.name || entry.invoiceUrl === file.url,
    );

    if (existingEntry) {
      if (!existingEntry.invoiceUrl) {
        existingEntry.invoiceUrl = file.url;
      }
      return;
    }

    combined.push({
      meetingId: "",
      contactId: "",
      type: inferInvoiceTypeFromFilename(file.name),
      amount: 0,
      currency: "USD",
      invoice: file.name,
      invoiceUrl: file.url,
      dateSent: file.dateCreated,
    });
  });

  return combined.sort((left, right) => {
    const leftDate = left.dateSent ? new Date(left.dateSent).getTime() : 0;
    const rightDate = right.dateSent ? new Date(right.dateSent).getTime() : 0;
    return rightDate - leftDate;
  });
};

const getEventDayKey = (event: IEvent): string | null => {
  if (!event.startDate) {
    return null;
  }

  return getDayKeyInTimezone(event.startDate, event.timeZone);
};

const formatDayKey = (
  dayKey: string | null,
  separator: "/" | "-",
): string => {
  if (!dayKey) {
    return "Date TBD";
  }

  const [year, month, day] = dayKey.split("-");
  if (!year || !month || !day) {
    return "Date TBD";
  }

  return `${month}${separator}${day}${separator}${year}`;
};

const compareText = (
  left: string,
  right: string,
  order: "asc" | "desc",
): number => {
  const leftValue = left.trim().toLowerCase();
  const rightValue = right.trim().toLowerCase();

  if (!leftValue && !rightValue) {
    return 0;
  }

  if (!leftValue) {
    return 1;
  }

  if (!rightValue) {
    return -1;
  }

  const compared = leftValue.localeCompare(rightValue);
  return order === "asc" ? compared : compared * -1;
};

const compareDate = (
  left: MyEventRow,
  right: MyEventRow,
  order: "asc" | "desc",
): number => {
  const leftHasDate = typeof left.sortDate === "number";
  const rightHasDate = typeof right.sortDate === "number";

  if (!leftHasDate && !rightHasDate) {
    return compareText(left.eventName, right.eventName, "asc");
  }

  if (!leftHasDate) {
    return 1;
  }

  if (!rightHasDate) {
    return -1;
  }

  const leftValue = left.sortDate as number;
  const rightValue = right.sortDate as number;

  if (leftValue === rightValue) {
    return compareText(left.eventName, right.eventName, "asc");
  }

  return order === "asc" ? leftValue - rightValue : rightValue - leftValue;
};

const sortRows = (
  rows: MyEventRow[],
  sorting: ISorting,
): MyEventRow[] => {
  return [...rows].sort((left, right) => {
    switch (sorting.field) {
      case "eventName":
        return compareText(left.eventName, right.eventName, sorting.order);
      case "location":
        return compareText(left.location, right.location, sorting.order);
      case "status": {
        const weight: Record<UiPaymentStatus, number> = {
          Unpaid: 0,
          Partial: 1,
          Paid: 2,
        };
        const leftWeight = weight[left.uiStatus];
        const rightWeight = weight[right.uiStatus];
        if (leftWeight === rightWeight) {
          return compareText(left.eventName, right.eventName, "asc");
        }
        return sorting.order === "asc"
          ? leftWeight - rightWeight
          : rightWeight - leftWeight;
      }
      case "eventDate":
      default:
        return compareDate(left, right, sorting.order);
    }
  });
};

const sanitizeFilePart = (value: string): string =>
  value
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, " ")
    .trim();

const buildRegistrationName = (registration: IRegistration): string => {
  const pieces = [registration.personName, registration.personLastName]
    .map((piece) => piece?.trim())
    .filter(Boolean) as string[];

  const uniquePieces = pieces.filter(
    (piece, index) =>
      pieces.findIndex(
        (candidate) => candidate.toLowerCase() === piece.toLowerCase(),
      ) === index,
  );

  return (
    uniquePieces.join(" ").trim() ||
    registration.badgeName?.trim() ||
    "Attendee"
  );
};

function DownloadIcon({ className = "h-[18px] w-[18px]" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 3v11" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M4 21h16" />
    </svg>
  );
}

function CreditCardIcon({ className = "h-[18px] w-[18px]" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
      <path d="M2.5 10h19" />
      <path d="M7 15h3" />
    </svg>
  );
}

function PeopleIcon({ className = "h-[18px] w-[18px]" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function StatusBadge({ status }: { status: UiPaymentStatus }) {
  const classes: Record<UiPaymentStatus, string> = {
    Paid: "border-[#d9f2e3] bg-[#effaf3] text-[#157347]",
    Partial: "border-[#f6e0b8] bg-[#fff7e8] text-[#9a6700]",
    Unpaid: "border-[#f3d2cf] bg-[#fdf0ef] text-[#c83c27]",
  };

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${classes[status]}`}
    >
      {status}
    </span>
  );
}

function ActionIconButton({
  label,
  onClick,
  children,
  disabled = false,
  tone = "default",
  className = "",
}: {
  label: string;
  onClick?: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  tone?: "default" | "danger";
  className?: string;
}) {
  const toneClassName =
    tone === "danger"
      ? "bg-transparent text-gray-font hover:bg-[#fff1ee] hover:text-[#b7311d] focus-visible:ring-[#cf3a25]/20"
      : "bg-transparent text-gray-font hover:bg-[#f3f5f7] hover:text-[#c83c27] focus-visible:ring-[#c83c27]/15";

  return (
    <button
      type="button"
      aria-label={label}
      title={disabled ? undefined : label}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 ${
        disabled
          ? "cursor-not-allowed bg-transparent text-[#a7b0b8]"
          : toneClassName
      } ${className}`}
    >
      {children}
    </button>
  );
}

export default function MyEventsPage() {
  const navigate = useNavigate();
  const { loggedPerson } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<MyEventRow[]>([]);
  const [sortingByTab, setSortingByTab] = useState<
    Record<MyEventsTab, ISorting>
  >(DEFAULT_SORTING_BY_TAB);
  const [paginationByTab, setPaginationByTab] = useState<
    Record<MyEventsTab, IPagination2>
  >({
    current: DEFAULT_PAGINATION,
    past: DEFAULT_PAGINATION,
  });
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    string | null
  >(null);
  const [downloadingAttendeesEventId, setDownloadingAttendeesEventId] =
    useState<string | null>(null);

  useEffect(() => {
    setPaginationByTab((previous) => ({
      current: {
        ...previous.current,
        pageIndex: 0,
      },
      past: {
        ...previous.past,
        pageIndex: 0,
      },
    }));
  }, [search]);

  const loadRows = useCallback(async () => {
    if (!loggedPerson?.id) {
      setRows([]);
      setLoading(false);
      setLoadError(null);
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      const registrations = await getRegistrationsByPerson(loggedPerson.id);
      const registrationsByEvent = mergeRegistrationsByEvent(registrations || []);
      const eventIds = Array.from(registrationsByEvent.keys());

      if (eventIds.length === 0) {
        setRows([]);
        return;
      }

      const events = await Promise.all(
        eventIds.map((eventId) => getEventById(eventId, false).catch(() => null)),
      );

      const nextRows = events
        .map((event) => {
          if (!event?.id || event.isDeleted === true) {
            return null;
          }

          const registration = registrationsByEvent.get(event.id);
          if (!registration) {
            return null;
          }

          const eventDayKey = getEventDayKey(event);
          const eventName =
            event.title?.trim() || registration.eventName?.trim() || "Event";

          const eventDateText = event.comingSoonDate
            ? formatEventDate(event)
            : formatDayKey(eventDayKey, "/");

          const row: MyEventRow = {
            id: registration.id || `${event.id}-${registration.registrationDate || 0}`,
            event,
            registration,
            eventName,
            eventDate: eventDateText,
            eventDateFilename: event.comingSoonDate
              ? eventDateText.replace(/\s+/g, "-")
              : formatDayKey(eventDayKey, "-"),
            location: formatEventLocation(event),
            uiStatus: resolveUiPaymentStatus(registration.paymentStatus),
            sortDate: event.startDate || null,
            currentSearchText: [
              eventName,
              eventDateText,
              formatEventLocation(event),
              resolveUiPaymentStatus(registration.paymentStatus),
            ]
              .join(" ")
              .toLowerCase(),
            pastSearchText: [eventName, eventDateText]
              .join(" ")
              .toLowerCase(),
          };

          return row;
        })
        .filter((row): row is MyEventRow => row !== null);

      setRows(nextRows);
    } catch (error) {
      console.error("Error loading My Events page:", error);
      setRows([]);
      setLoadError(
        "We could not load your event registrations right now. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [loggedPerson?.id]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const currentRows = useMemo(
    () => rows.filter((row) => isCurrentEvent(row.event)),
    [rows],
  );

  const pastRows = useMemo(
    () => rows.filter((row) => !isCurrentEvent(row.event)),
    [rows],
  );

  const filteredCurrentRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return currentRows;
    }

    return currentRows.filter((row) =>
      row.currentSearchText.includes(normalizedSearch),
    );
  }, [currentRows, search]);

  const filteredPastRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return pastRows;
    }

    return pastRows.filter((row) =>
      row.pastSearchText.includes(normalizedSearch),
    );
  }, [pastRows, search]);

  const sortedCurrentRows = useMemo(
    () => sortRows(filteredCurrentRows, sortingByTab.current),
    [filteredCurrentRows, sortingByTab.current],
  );

  const sortedPastRows = useMemo(
    () => sortRows(filteredPastRows, sortingByTab.past),
    [filteredPastRows, sortingByTab.past],
  );

  const paginatedCurrentRows = useMemo(() => {
    const pagination = paginationByTab.current;
    const startIndex = pagination.pageIndex * pagination.pageSize;
    return sortedCurrentRows
      .slice(startIndex, startIndex + pagination.pageSize)
      .map((row, index) => ({
        ...row,
        rowNumber: startIndex + index + 1,
      }));
  }, [
    paginationByTab.current.pageIndex,
    paginationByTab.current.pageSize,
    sortedCurrentRows,
  ]);

  const paginatedPastRows = useMemo(() => {
    const pagination = paginationByTab.past;
    const startIndex = pagination.pageIndex * pagination.pageSize;
    return sortedPastRows
      .slice(startIndex, startIndex + pagination.pageSize)
      .map((row, index) => ({
        ...row,
        rowNumber: startIndex + index + 1,
      }));
  }, [
    paginationByTab.past.pageIndex,
    paginationByTab.past.pageSize,
    sortedPastRows,
  ]);

  const handleSortingChange = useCallback(
    (tab: MyEventsTab, nextSorting: ISorting) => {
      setSortingByTab((previous) => ({
        ...previous,
        [tab]: nextSorting,
      }));
    },
    [],
  );

  const handlePaginationChange = useCallback(
    (tab: MyEventsTab, nextPagination: IPagination2) => {
      setPaginationByTab((previous) => ({
        ...previous,
        [tab]: nextPagination,
      }));
    },
    [],
  );

  const handleCurrentSortingChange = useCallback(
    (nextSorting: ISorting) => {
      handleSortingChange("current", nextSorting);
    },
    [handleSortingChange],
  );

  const handlePastSortingChange = useCallback(
    (nextSorting: ISorting) => {
      handleSortingChange("past", nextSorting);
    },
    [handleSortingChange],
  );

  const handleCurrentPaginationChange = useCallback(
    (nextPagination: IPagination2) => {
      handlePaginationChange("current", nextPagination);
    },
    [handlePaginationChange],
  );

  const handlePastPaginationChange = useCallback(
    (nextPagination: IPagination2) => {
      handlePaginationChange("past", nextPagination);
    },
    [handlePaginationChange],
  );

  const handleDownloadInvoice = useCallback(
    async (row: MyEventRow) => {
      if (!row.event.id || !row.registration.id || !loggedPerson?.id) {
        toastWarn("Invoice file is not available for this registration.");
        return;
      }

      try {
        setDownloadingInvoiceId(row.registration.id);

        const [history, blobFiles] = await Promise.all([
          getInvoiceHistory(row.event.id, row.registration.id),
          listInvoiceFiles(loggedPerson.id, row.event.id),
        ]);

        const mergedEntries = mergeInvoiceEntries(
          history.map((entry) => ({
            meetingId: entry.meetingId,
            contactId: entry.contactId,
            type: entry.type,
            amount: entry.amount,
            currency: entry.currency,
            invoice: entry.invoice,
            invoiceUrl: entry.invoiceUrl,
            dateSent: entry.dateSent,
          })),
          blobFiles.map((file) => ({
            name: file.name,
            url: file.url,
            dateCreated: file.dateCreated,
          })),
        );

        const desiredType = getInvoiceTypeForStatus(
          row.registration.paymentStatus,
        );
        const latestInvoice = mergedEntries.find(
          (entry) =>
            entry.type === desiredType &&
            entry.invoiceUrl &&
            entry.invoice &&
            entry.invoice.trim().length > 0,
        );

        if (!latestInvoice?.invoiceUrl || !latestInvoice.invoice) {
          toastWarn(`No ${desiredType.toLowerCase()} invoice is available yet.`);
          return;
        }

        await downloadInvoicePDF(latestInvoice.invoiceUrl, latestInvoice.invoice);
      } catch (error: any) {
        console.error("Error downloading invoice:", error);
        toastError(
          error?.message || "The invoice could not be downloaded right now.",
        );
      } finally {
        setDownloadingInvoiceId(null);
      }
    },
    [loggedPerson?.id],
  );

  const handleDownloadAttendees = useCallback(async (row: MyEventRow) => {
    if (!row.event.id) {
      toastWarn("Attendees list is not available for this event.");
      return;
    }

    try {
      setDownloadingAttendeesEventId(row.event.id);

      const registrations = await getRegistrationsByEvent(row.event.id);
      const attendeeRows = registrations
        .filter(isActiveRegistration)
        .filter(
          (registration) =>
            !EXCLUDED_ATTENDEE_FIRM_IDS.has((registration.firmId || "").trim()),
        )
        .map((registration) => ({
          Name: buildRegistrationName(registration),
          Firm: registration.firmName?.trim() || "-",
          Jurisdiction:
            jurisdictionValueToLabel(registration.firmJurisdiction) || "-",
        }))
        .sort((left, right) => left.Name.localeCompare(right.Name));

      if (attendeeRows.length === 0) {
        toastWarn("No attendees are available to export for this event.");
        return;
      }

      const safeName = sanitizeFilePart(row.eventName) || "Event";
      const safeDate =
        row.eventDateFilename === "Date TBD" ? "TBD" : row.eventDateFilename;

      await exportStyledExcel({
        filename: `${safeName} - ${safeDate}.xlsx`,
        title: row.eventName,
        documentType: "Attendees List",
        sheetName: "Attendees",
        columns: [
          { key: "Name", header: "Name", width: 28 },
          { key: "Firm", header: "Firm", width: 28 },
          { key: "Jurisdiction", header: "Jurisdiction", width: 20 },
        ],
        rows: attendeeRows,
      });
    } catch (error: any) {
      console.error("Error exporting attendees list:", error);
      toastError(
        error?.message ||
          "The attendees list could not be downloaded right now.",
      );
    } finally {
      setDownloadingAttendeesEventId(null);
    }
  }, []);

  const handleModifyRegistration = useCallback(
    (row: MyEventRow) => {
      navigate(`/events/${row.event.id}/registration/form`);
    },
    [navigate],
  );

  const handlePayRegistration = useCallback(
    (row: MyEventRow) => {
      navigate(`/events/${row.event.id}/registration/payment`);
    },
    [navigate],
  );

  const currentColumns = useMemo<IHeader[]>(
    () => [
      {
        id: "rowNumber",
        header: <div className="flex w-full justify-center">#</div>,
        width: 40,
        enableSorting: false,
        cell: ({ value }) => <div className="text-center">{value}</div>,
      },
      {
        id: "eventName",
        header: "Event Name",
        width: 260,
        cell: ({ row }) => (
          <Link
            to={`/events/${row.event.id}`}
            className="block max-w-[24rem] whitespace-normal break-words font-medium leading-snug text-gray-font no-underline hover:text-Default hover:underline"
          >
            {row.eventName}
          </Link>
        ),
      },
      {
        id: "eventDate",
        header: "Event Date",
        width: 150,
      },
      {
        id: "location",
        header: "Location",
        width: 190,
      },
      {
        id: "status",
        header: "Status",
        width: 100,
        cell: ({ row }) => <StatusBadge status={row.uiStatus} />,
      },
      {
        id: "actions",
        header: "Actions",
        width: 130,
        enableSorting: false,
        cell: ({ row }) => {
          const paymentEnabled = isPaymentDue(row.registration.paymentStatus);
          const isDownloadingInvoice = downloadingInvoiceId === row.registration.id;
          const isDownloadingAttendees =
            downloadingAttendeesEventId === row.event.id;

          return (
            <div className="ml-auto flex w-[124px] items-center gap-1">
              <ActionIconButton
                label="Modify registration"
                onClick={() => handleModifyRegistration(row)}
                className="h-7 w-7"
              >
                <span className="h-[18px] w-[18px] text-current">
                  <PencilEdit className="h-full w-full" />
                </span>
              </ActionIconButton>
              <ActionIconButton
                label={
                  isDownloadingInvoice ? "Downloading invoice..." : "Download invoice"
                }
                onClick={() => {
                  void handleDownloadInvoice(row);
                }}
                disabled={isDownloadingInvoice}
                className="h-7 w-7"
              >
                <DownloadIcon />
              </ActionIconButton>
              <ActionIconButton
                label={
                  isDownloadingAttendees
                    ? "Downloading attendees list..."
                    : "Download attendees list"
                }
                onClick={() => {
                  void handleDownloadAttendees(row);
                }}
                disabled={isDownloadingAttendees}
                className="h-7 w-7"
              >
                <span className="h-[18px] w-[18px] text-current">
                  <PeopleIcon className="h-full w-full" />
                </span>
              </ActionIconButton>
              <ActionIconButton
                label="Complete payment"
                onClick={() => handlePayRegistration(row)}
                disabled={!paymentEnabled}
                tone="danger"
                className="h-7 w-7"
              >
                <span className="h-[18px] w-[18px] text-current">
                  {paymentEnabled ? <CreditCardIcon /> : <LockOutlined />}
                </span>
              </ActionIconButton>
            </div>
          );
        },
      },
    ],
    [
      downloadingAttendeesEventId,
      downloadingInvoiceId,
      handleDownloadAttendees,
      handleDownloadInvoice,
      handleModifyRegistration,
      handlePayRegistration,
    ],
  );

  const pastColumns = useMemo<IHeader[]>(
    () => [
      {
        id: "rowNumber",
        header: <div className="flex w-full justify-center">#</div>,
        width: 40,
        enableSorting: false,
        cell: ({ value }) => <div className="text-center">{value}</div>,
      },
      {
        id: "eventName",
        header: "Event Name",
        width: 420,
        cell: ({ row }) => (
          <Link
            to={`/events/${row.event.id}`}
            className="block max-w-[28rem] whitespace-normal break-words font-medium leading-snug text-gray-font no-underline hover:text-Default hover:underline"
          >
            {row.eventName}
          </Link>
        ),
      },
      {
        id: "eventDate",
        header: "Event Date",
        width: 160,
      },
      {
        id: "actions",
        header: "Actions",
        width: 100,
        enableSorting: false,
        cell: ({ row }) => {
          const isDownloadingAttendees =
            downloadingAttendeesEventId === row.event.id;

          return (
            <div className="flex items-center">
              <ActionIconButton
                label={
                  isDownloadingAttendees
                    ? "Downloading attendees list..."
                    : "Download attendees list"
                }
                onClick={() => {
                  void handleDownloadAttendees(row);
                }}
                disabled={isDownloadingAttendees}
              >
                <span className="h-[18px] w-[18px] text-current">
                  <PeopleIcon className="h-full w-full" />
                </span>
              </ActionIconButton>
            </div>
          );
        },
      },
    ],
    [downloadingAttendeesEventId, handleDownloadAttendees],
  );

  const searchPlaceholder = "Search by Event, Date, Location or Status";

  return (
    <div className="w-full bg-white px-6 py-8 lg:px-10 lg:py-10">
      <style>{`
        .my-events-table .sticky.top-0.z-20,
        .my-events-table th.sticky.top-0 {
          background-color: #c83c27 !important;
        }

        .my-events-table th[data-col-id="rowNumber"] > div {
          justify-content: center !important;
        }

        .my-events-table th[data-col-id="rowNumber"] > div > div:first-child {
          width: 100%;
          justify-content: center !important;
        }

        .my-events-table-current th[data-col-id="actions"] > div {
          justify-content: flex-end !important;
        }

        .my-events-table-current th[data-col-id="actions"] > div > div:first-child {
          width: 124px !important;
          min-width: 124px !important;
          max-width: 124px !important;
          flex: 0 0 124px !important;
          justify-content: flex-start !important;
        }

        .my-events-table-current th[data-col-id="actions"] > div > div:last-child {
          display: none !important;
        }

        .my-events-table th.sticky.top-0 {
          padding-top: 0.875rem !important;
          padding-bottom: 0.875rem !important;
          font-size: 17px !important;
        }

        .my-events-table tbody td {
          padding-top: 0.9rem !important;
          padding-bottom: 0.9rem !important;
          font-size: 17px !important;
        }
      `}</style>
      <div className="w-full">
        <h1 className="mb-6 origin-bottom text-left font-decimal text-xl font-medium text-primary [transform:scaleY(1.0)] lg:text-3xl">
          My Events
        </h1>

        <div className="flex flex-col gap-8">
          <div className="w-full md:max-w-xl">
            <InputText
              value={search}
              onChange={setSearch}
              type="search"
              placeholder={searchPlaceholder}
            />
          </div>

          <section>
            {loadError ? (
              <div className="rounded-lg border border-gray-200 px-6 py-8">
                <div className="mb-4 text-sm text-[#c83c27]">{loadError}</div>
                <button
                  type="button"
                  onClick={() => {
                    void loadRows();
                  }}
                  className="rounded-md bg-[#c83c27] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#ab341f]"
                >
                  Retry
                </button>
              </div>
            ) : loading ? (
              <div className="rounded-lg border border-gray-200 px-6 py-10 text-sm text-[#69717a]">
                Loading your event registrations...
              </div>
            ) : (
              <div className="flex flex-col gap-10">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <h3 className="roboto-font text-left text-[20px] font-medium leading-[1.2] text-Default">
                      Current Registrations
                    </h3>
                  </div>

                  <div className="my-events-table my-events-table-current overflow-hidden border border-gray-200">
                    <PaginatedTable
                      key="current"
                      tableKey="/my-events/current"
                      data={paginatedCurrentRows}
                      totalCount={sortedCurrentRows.length}
                      columnsDef={currentColumns}
                      sorting={sortingByTab.current}
                      pagination={paginationByTab.current}
                      onSortingChange={handleCurrentSortingChange}
                      onPaginationChange={handleCurrentPaginationChange}
                      showColumnVisibility={false}
                      showExcelExport={false}
                      emptyStateMessage={
                        search.trim()
                          ? "No current registrations match your search."
                          : "No current registrations found."
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <h3 className="roboto-font text-left text-[20px] font-medium leading-[1.2] text-Default">
                      Past Events
                    </h3>
                  </div>

                  <div className="my-events-table overflow-hidden border border-gray-200">
                    <PaginatedTable
                      key="past"
                      tableKey="/my-events/past"
                      data={paginatedPastRows}
                      totalCount={sortedPastRows.length}
                      columnsDef={pastColumns}
                      sorting={sortingByTab.past}
                      pagination={paginationByTab.past}
                      onSortingChange={handlePastSortingChange}
                      onPaginationChange={handlePastPaginationChange}
                      showColumnVisibility={false}
                      showExcelExport={false}
                      emptyStateMessage={
                        search.trim()
                          ? "No past events match your search."
                          : "No past events found."
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
